import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCall } from "@/context/CallContext";
import { useAuth } from "@/context/AuthContext";
import { PhoneIncoming, PhoneOff, Phone } from "lucide-react";
import { Avatar } from "@/components/Avatar";

const FAIL_TEXT = { offline: "is offline right now", busy: "is in another call", declined: "declined the call", unanswered: "didn't answer", not_friends: "isn't on your friends list", expired: "call expired", busy_self: "— finish your current call first" };

export function CallEvents() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const { incomingCall, acceptCall, declineCall, lastEvent, calling } = useCall();

  useEffect(() => {
    if (!lastEvent) return;
    if (lastEvent.type === "friend_request") toast(`${lastEvent.who?.flag || ""} ${lastEvent.who?.name} wants to be friends`, { action: { label: "View", onClick: () => nav("/dashboard?tab=friends") } });
    if (lastEvent.type === "friend_accepted") toast.success(`${lastEvent.who?.name} accepted your friend request`);
    if (lastEvent.type === "friend_message") toast(`${lastEvent.who?.flag || ""} ${lastEvent.who?.name}: ${lastEvent.text}`, { action: { label: "Reply", onClick: () => nav(`/dashboard?tab=friends&thread=${lastEvent.who?.id}`) }, duration: 7000 });
    if (lastEvent.type === "streak") toast.success(`🔥 Day ${lastEvent.count} streak!${lastEvent.count === lastEvent.best && lastEvent.count > 1 ? " New personal best" : ""}`, { duration: 6000 });
    if (lastEvent.type === "call_failed") toast.error(`${calling?.friend?.name || "Friend"} ${FAIL_TEXT[lastEvent.reason] || "is unavailable"}`);
    if (lastEvent.type === "call_cancelled") toast("Missed call");
    if (lastEvent.type === "moderation_notice") {
      if (lastEvent.action === "kick") { toast.error(lastEvent.message, { duration: 8000 }); logout().then(() => nav("/auth")); }
      else toast.warning(lastEvent.message, { duration: 8000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastEvent]);

  if (!incomingCall) return null;
  const f = incomingCall.from;
  return (
    <div className="fixed inset-0 z-[60] bg-slate-950/80 backdrop-blur flex items-center justify-center p-4" data-testid="incoming-call-modal">
      <div className="orbit-glass rounded-3xl p-8 max-w-sm w-full text-center border border-cyan-500/30 orbit-fade-up">
        <div className="w-20 h-20 mx-auto rounded-full p-[3px] bg-gradient-to-br from-cyan-400 to-fuchsia-500 orbit-glow-cyan">
          <Avatar profile={f} size="w-full h-full" text="text-3xl" className="bg-slate-950" testId="incoming-call-avatar" />
        </div>
        <div className="text-xs font-mono uppercase tracking-widest text-cyan-300 mt-5 flex items-center justify-center gap-1"><PhoneIncoming className="w-3 h-3" /> Incoming call</div>
        <div className="font-display text-2xl font-bold mt-1" data-testid="incoming-call-from">{f.flag} {f.name}</div>
        <div className="text-sm text-slate-400 mt-1">{f.country} · friend</div>
        <div className="flex gap-3 mt-6">
          <button onClick={declineCall} className="flex-1 py-3 rounded-full bg-rose-500/10 border border-rose-500/40 text-rose-300 hover:bg-rose-500/20 flex items-center justify-center gap-2" data-testid="incoming-call-decline"><PhoneOff className="w-4 h-4" /> Decline</button>
          <button onClick={async () => { await acceptCall(); nav("/chat"); }} className="flex-1 orbit-btn-primary flex items-center justify-center gap-2" data-testid="incoming-call-accept"><Phone className="w-4 h-4" /> Accept</button>
        </div>
      </div>
    </div>
  );
}
